// Uploading invoices: PDFs and zips of PDFs, up to 100 MB each, with the upload's progress shown while the bytes
// go and, for a zip, the unpacking's progress after that ("37 of 120"), then what came of it. The hook does the
// sending; the panel shows every upload still worth looking at until it is dismissed.
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ApiError, apiUpload } from "../api/client";
import { useDismissUpload, useInvoices, useLiveInvoices, useSeedUploadJobs, useUploadJob, useUploads } from "../api/hooks";
import type { UploadJob, UploadResult } from "../api/types";
import { Button, Icon, Progress } from "../ui";
import { CheckIcon, Spinner, muted } from "./components";
import { fmtDay, outcomeOf } from "./format";

export const MAX_UPLOAD_BYTES = 100 * 1024 * 1024;

// Extensions only: the file dialog lists every entry, and MIME types beside them read as duplicates. The hook
// still checks each chosen file by name and type (isPdf, isZip), so nothing is lost.
const ACCEPT = ".pdf,.zip";

export type UploadItem =
  | { key: string; kind: "sending"; fileName: string; loaded: number; total: number; cancel: () => void }
  | { key: string; kind: "unpacking"; fileName: string; jobId: string }
  | { key: string; kind: "failed"; fileName: string; message: string };

const isZip = (f: File) => /\.zip$/i.test(f.name) || f.type === "application/zip" || f.type === "application/x-zip-compressed";
const isPdf = (f: File) => /\.pdf$/i.test(f.name) || f.type === "application/pdf";

export function mb(bytes: number): string {
  const n = bytes / 1048576;
  return (n >= 10 ? n.toFixed(0) : n.toFixed(1)) + " MB";
}

/**
 * Sends files to /intake/upload: PDFs together in one request, each zip in its own, every request with a progress
 * bar. `onPdfs` hears the result for the PDFs so the screen can say what happened to them; zips show up in the
 * panel's items as they unpack.
 */
export function useUploader(onPdfs?: (result: UploadResult) => void) {
  const queryClient = useQueryClient();
  const dismissUpload = useDismissUpload();
  const [items, setItems] = useState<UploadItem[]>([]);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const nextKey = useRef(0);

  // An upload the server took in is dismissed there too, so it doesn't come back as a recent upload.
  const dismiss = useCallback((key: string) => {
    const item = itemsRef.current.find((i) => i.key === key);
    if (item?.kind === "unpacking") dismissUpload(item.jobId);
    setItems((list) => list.filter((i) => i.key !== key));
  }, [dismissUpload]);

  const send = useCallback(async (files: File[], key: string) => {
    const form = new FormData();
    for (const f of files) form.append("files", f, f.name);
    const controller = new AbortController();
    const fileName = files.length === 1 ? files[0]!.name : `${files.length} PDFs`;
    const total = files.reduce((n, f) => n + f.size, 0);
    setItems((list) => [...list, { key, kind: "sending", fileName, loaded: 0, total, cancel: () => controller.abort() }]);
    try {
      const result = await apiUpload<UploadResult>("/intake/upload", form, (loaded, size) => {
        setItems((list) => list.map((i) => (i.key === key && i.kind === "sending" ? { ...i, loaded, total: size } : i)));
      }, controller.signal);
      setItems((list) => [
        ...list.filter((i) => i.key !== key),
        ...result.uploads.map((u) => ({ key: `${key}:${u.id}`, kind: "unpacking" as const, fileName: u.fileName, jobId: u.id })),
      ]);
      if (result.accepted.length || result.duplicates.length || result.rejected.length) onPdfs?.(result);
      void queryClient.invalidateQueries({ queryKey: ["invoices"] });
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      void queryClient.invalidateQueries({ queryKey: ["needs"] });
      void queryClient.invalidateQueries({ queryKey: ["uploads"] });
    } catch (e) {
      if (e instanceof DOMException && e.name === "AbortError") {
        setItems((list) => list.filter((i) => i.key !== key));
        return;
      }
      const message = e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e);
      setItems((list) => list.map((i) => (i.key === key ? { key, kind: "failed" as const, fileName, message } : i)));
    }
  }, [onPdfs, queryClient]);

  const start = useCallback((input: FileList | File[] | null) => {
    const files = Array.from(input ?? []);
    if (!files.length) return;
    const pdfs: File[] = [];
    for (const f of files) {
      const key = String(++nextKey.current);
      if (f.size > MAX_UPLOAD_BYTES) {
        setItems((list) => [...list, { key, kind: "failed", fileName: f.name, message: `Larger than ${mb(MAX_UPLOAD_BYTES)}` }]);
      } else if (isZip(f)) {
        void send([f], key);
      } else if (isPdf(f)) {
        pdfs.push(f);
      } else {
        setItems((list) => [...list, { key, kind: "failed", fileName: f.name, message: "Not a PDF or a zip of PDFs" }]);
      }
    }
    if (pdfs.length) void send(pdfs, String(++nextKey.current));
  }, [send]);

  const busy = items.some((i) => i.kind === "sending");
  return { items, start, dismiss, busy, accept: ACCEPT };
}

const RECENT_MS = 24 * 3600_000;

/**
 * The person's own uploads from the last day as panel rows, so what came of an upload (and which files were already
 * received) is still there after the toast and after a reload, until they dismiss it; a dismissed upload stays gone
 * on every browser. Rows this page is already showing from its own uploads are left out.
 */
export function useRecentUploads(own: UploadItem[]): { items: UploadItem[]; dismiss: (key: string) => void } {
  const recent = useUploads(10);
  // The list already holds each job: its card renders from that at once, with no spinner while the job is fetched again by id.
  useSeedUploadJobs(recent.data);
  const dismissUpload = useDismissUpload();
  const ownIds = own.map((i) => (i.kind === "unpacking" ? i.jobId : "")).filter(Boolean);
  const ownKey = ownIds.join(",");
  const items = useMemo(() => {
    const cutoff = Date.now() - RECENT_MS;
    return (recent.data ?? [])
      .filter((j) => !ownIds.includes(j.id) && new Date(j.finishedAt ?? j.createdAt).getTime() > cutoff)
      .map((j): UploadItem => ({ key: "job:" + j.id, kind: "unpacking", fileName: j.fileName, jobId: j.id }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recent.data, ownKey]);
  const dismiss = useCallback((key: string) => dismissUpload(key.replace(/^job:/, "")), [dismissUpload]);
  return { items, dismiss };
}

/** The uploads in flight and just finished, one row each. Nothing is rendered when there is nothing to show. */
export function UploadPanel({ items, onDismiss, style }: { items: UploadItem[]; onDismiss: (key: string) => void; style?: React.CSSProperties }) {
  if (!items.length) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, ...style }}>
      {items.map((item) => (
        <div key={item.key} style={{ padding: "12px 16px", borderRadius: "var(--radius-lg)", border: "1px solid var(--border-subtle)", background: "var(--surface-card)", boxShadow: "var(--shadow-xs)" }}>
          {item.kind === "sending" && <Sending item={item} />}
          {item.kind === "unpacking" && <Unpacking item={item} onDismiss={() => onDismiss(item.key)} />}
          {item.kind === "failed" && (
            <Row icon={<CheckIcon r="no" size={18} />} lead={item.fileName} rest={`– not accepted: ${item.message.charAt(0).toLowerCase()}${item.message.slice(1)}`} action={<Dismiss onClick={() => onDismiss(item.key)} />} />
          )}
        </div>
      ))}
    </div>
  );
}

function Sending({ item }: { item: Extract<UploadItem, { kind: "sending" }> }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <Row icon={<Spinner size={18} />} lead={`Uploading ${item.fileName}`} rest={`– ${mb(item.loaded)} of ${mb(item.total)}`} action={<Button size="sm" variant="ghost" onClick={item.cancel}>Cancel</Button>} />
      <Progress value={item.loaded} max={item.total} showValue={false} size="sm" />
    </div>
  );
}

function Unpacking({ item, onDismiss }: { item: Extract<UploadItem, { kind: "unpacking" }>; onDismiss: () => void }) {
  const job = useUploadJob(item.jobId);
  const queryClient = useQueryClient();
  const status = job.data?.status;
  useEffect(() => {
    if (status && status !== "Unpacking") {
      void queryClient.invalidateQueries({ queryKey: ["invoices"] });
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      void queryClient.invalidateQueries({ queryKey: ["needs"] });
    }
  }, [status, queryClient]);

  const j = job.data;
  if (!j) return <Row icon={<Spinner size={18} />} lead={item.fileName} rest="– unpacking" />;
  if (j.status === "Unpacking") {
    const soFar = j.accepted ? ` – ${invoices(j.accepted)} queued so far` : "";
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <Row icon={<Spinner size={18} />} lead={j.fileName} rest={j.total ? `– unpacking, ${j.read} of ${j.total} files${soFar}` : "– opening the zip…"} />
        <Progress value={j.read} max={Math.max(1, j.total)} showValue={false} size="sm" />
      </div>
    );
  }
  return <Finished job={j} onDismiss={onDismiss} />;
}

const invoices = (n: number) => `${n} invoice${n === 1 ? "" : "s"}`;
/** What the invoice a duplicate matched was doing: "Approved today", "Ready to approve", "Comparing", "Queued". */
function matchedState(d: { status?: string | null; awaitingRelease?: boolean; approvedAt?: string | null }): string {
  if (d.approvedAt) { const day = fmtDay(d.approvedAt); return `Approved ${day === "Today" ? "today" : day === "Yesterday" ? "yesterday" : day}`; }
  if (d.status === "Vouched") return "Approved";
  if (d.awaitingRelease) return "Ready to approve";
  if (d.status === "Processing") return "Comparing";
  return d.status ? "Queued" : "already received";
}
const files = (n: number) => `${n} file${n === 1 ? "" : "s"}`;

/**
 * One sentence per upload, led by the count (lo-fi "Upload rows on the Invoice Queue"): "8 invoices queued",
 * "5 of 8 invoices queued – 2 already received – 1 not accepted", "8 invoices already received – nothing queued".
 * A file is named only when there is one real file: a single PDF, or the zip.
 */
const same0 = (j: UploadJob) => j.details?.duplicates?.[0] ?? null;

function sentence(j: UploadJob): { lead: string; rest: string; r: "ok" | "adj" | "no" } {
  const named = /\.(pdf|zip)$/i.test(j.fileName);
  const r: "ok" | "adj" | "no" = j.status === "Failed" ? "no" : j.accepted > 0 ? (j.rejected > 0 ? "adj" : "ok") : j.duplicates > 0 ? "adj" : "no";
  if (j.status === "Failed" && !j.found) return { lead: j.fileName, rest: `– couldn’t be unpacked${j.details?.problems[0] ? ": " + j.details.problems[0].replace(/^.*?couldn['’]t be unpacked \(?/, "").replace(/\)$/, "") : ""}`, r };
  const tail = [j.duplicates ? `${j.duplicates} already received` : "", j.rejected ? `${j.rejected} not accepted` : "", j.ignored ? `${files(j.ignored)} ignored` : ""].filter(Boolean);
  const after = tail.length ? "– " + tail.join(" – ") : "";
  if (named) {
    // A single PDF, or a zip: the file is the subject.
    if (j.found <= 1 && !/\.zip$/i.test(j.fileName)) return { lead: j.fileName, rest: j.accepted ? "– queued" : j.duplicates ? "– already received" + (same0(j)?.status ? ` – ${matchedState(same0(j)!)}` : "") : "– not accepted" + (j.details?.rejected[0]?.reason ? ": " + j.details.rejected[0].reason.charAt(0).toLowerCase() + j.details.rejected[0].reason.slice(1) : ""), r };
    const queued = j.accepted === j.found ? `${invoices(j.accepted)} queued` : j.accepted ? `${j.accepted} of ${invoices(j.found)} queued` : "nothing queued";
    return { lead: j.fileName, rest: `– ${queued}${after ? " " + after : ""}`, r };
  }
  if (j.accepted === j.found) return { lead: `${invoices(j.accepted)} queued`, rest: "", r };
  if (j.accepted > 0) return { lead: `${j.accepted} of ${invoices(j.found)} queued`, rest: after, r };
  if (j.duplicates === j.found) return { lead: `${invoices(j.duplicates)} already received`, rest: "– nothing queued", r };
  if (j.rejected === j.found) return { lead: `${files(j.rejected)} not accepted`, rest: "– nothing queued", r };
  return { lead: "Nothing queued", rest: after, r };
}

/**
 * What the invoices an upload took in are doing now, from the live state (patched by the hub as they move): how
 * many are still queued or being compared, and how many ended ready, needing a person or approved. The live state
 * holds everything changed in the last day, which a card's invoices are; any it doesn't hold are read once by id.
 * Uploads recorded before the job kept its accepted files have nothing to follow.
 */
function useTaken(j: UploadJob) {
  const taken = j.details?.accepted ?? [];
  const wanted = useMemo(() => new Set(taken.map((a) => a.invoiceId)), [taken]);
  const q = useLiveInvoices((i) => wanted.has(i.id), taken.length > 0);
  const missing = q.data ? [...wanted].filter((id) => !q.data!.items.some((i) => i.id === id)) : [];
  const rest = useInvoices({ ids: missing.join(","), pageSize: 200 }, missing.length > 0, 0);
  // The by-id read keeps its last answer as placeholder data after the live state catches up, so an invoice can be in
  // both: the live copy wins, and each invoice is counted once.
  const byId = new Map([...(rest.data?.items ?? []), ...(q.data?.items ?? [])].filter((i) => wanted.has(i.id)).map((i) => [i.id, i] as const));
  const items = [...byId.values()];
  const seen = new Set(byId.keys());
  // Every invoice lands in exactly one bucket, by the same outcome the rest of the portal uses, so the figures always
  // add up to what was taken in. "Retrying" is an invoice the worker set aside after a Syncore error, due again later.
  let queued = taken.filter((a) => !seen.has(a.invoiceId)).length, retrying = 0, comparing = 0, ready = 0, needs = 0, approved = 0, other = 0;
  for (const i of items) {
    switch (outcomeOf(i)) {
      case "ok": approved++; break;
      case "ready": ready++; break;
      case "rev": case "stop": case "set": needs++; break;
      case "prog": comparing++; break;
      case "wait": if (i.status === "Held") retrying++; else queued++; break;
      default: other++;
    }
  }
  const tracked = taken.length > 0;
  // "In flight" only once the lists have answered: before that the card shows the upload's plain sentence, so a reload
  // of a long-finished upload never flashes the figures.
  const loaded = !!q.data && (missing.length === 0 || !!rest.data);
  return { tracked, loaded, queued, retrying, comparing, ready, needs, approved, other, inFlight: tracked && loaded && queued + retrying + comparing > 0 };
}

/** Once every invoice an upload took in has finished: "5 of 6 invoices ready to approve – 1 needs you, 1 already received". */
function doneSentence(j: UploadJob, t: ReturnType<typeof useTaken>): { lead: string; rest: string; r: "ok" | "adj" | "no" } {
  const tail = [t.approved ? `${t.approved} approved` : "", t.needs ? `${t.needs} needs you` : "", t.other ? `${t.other} not approved` : "", j.duplicates ? `${j.duplicates} already received` : "", j.rejected ? `${j.rejected} not accepted` : "", j.ignored ? `${files(j.ignored)} ignored` : ""].filter(Boolean);
  const r: "ok" | "adj" | "no" = t.needs || t.other || j.rejected || j.duplicates ? "adj" : t.ready + t.approved > 0 ? "ok" : "no";
  if (j.found <= 1 && /\.pdf$/i.test(j.fileName)) return { lead: j.fileName, rest: `– ${t.ready ? "ready to approve" : t.approved ? "approved" : t.needs ? "needs you" : "not approved"}`, r };
  return { lead: `${t.ready} of ${invoices(j.found)} ready to approve`, rest: tail.length ? "– " + tail.join(", ") : "", r };
}

function Fig({ n, label, tone }: { n: number; label: string; tone: "" | "brand" | "good" | "warn" }) {
  return <div><b className={n === 0 ? "zero" : tone}>{n}</b>{label}</div>;
}

function Finished({ job: j, onDismiss }: { job: UploadJob; onDismiss: () => void }) {
  const t = useTaken(j);
  const base = sentence(j);
  // Every hook comes before the early return below, or the component gains hooks when processing finishes.
  const [showAll, setShowAll] = useState(false);
  const navigate = useNavigate();
  // While its invoices are still being read and compared, the card keeps the upload's sentence and shows the numbers.
  if (t.inFlight) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <Row icon={<Spinner size={18} />} lead={base.lead} rest={base.rest} />
        <div className="avp-figs" style={{ margin: "6px 0 2px 30px" }}>
          <Fig n={t.queued} label="Queued" tone="" />
          {t.retrying > 0 && <Fig n={t.retrying} label="Retrying" tone="warn" />}
          <Fig n={t.comparing} label="Comparing" tone="brand" />
          <Fig n={t.ready} label="Ready to approve" tone="good" />
          <Fig n={t.needs} label="Needs you" tone="warn" />
          {t.approved > 0 && <Fig n={t.approved} label="Approved" tone="good" />}
          {t.other > 0 && <Fig n={t.other} label="Not approved" tone="" />}
        </div>
      </div>
    );
  }
  const { lead, rest, r } = t.tracked && t.loaded ? doneSentence(j, t) : base;
  const single = j.found <= 1 && !/\.zip$/i.test(j.fileName);
  const same = j.details?.duplicates ?? [];
  const rejected = j.details?.rejected ?? [];
  const problems = j.details?.problems ?? [];
  const open = (id: string) => <a onClick={() => navigate(`/invoices/${id}`)} style={{ cursor: "pointer", font: "700 13px var(--font-body)" }}>Open the invoice</a>;
  // Under the sentence: which files were already received (each with its invoice) and which were not accepted (each
  // with why), five of each until "Show all"; a single file's one line is folded into the sentence instead.
  const lines: ReactNode[] = single
    ? (same[0]?.invoiceId ? [<span key="d">{open(same[0].invoiceId)}</span>] : [])
    : [
        ...(showAll ? same : same.slice(0, 5)).map((d) => <span key={"d" + d.fileName + (d.invoiceId ?? "")} style={{ display: "inline-flex", gap: 8, flexWrap: "wrap", alignItems: "baseline" }}>{d.fileName}: already received{d.status ? ` – ${matchedState(d)}` : ""}{d.invoiceId && open(d.invoiceId)}</span>),
        ...(showAll ? rejected : rejected.slice(0, 5)).map((x) => <span key={"r" + x.fileName}>{x.fileName}: {x.reason ?? "not accepted"}</span>),
        ...(j.status === "Failed" || j.found ? problems : []).map((n) => <span key={"p" + n}>{n}</span>),
      ];
  const hidden = same.length + rejected.length > 10 && !showAll;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <Row icon={<CheckIcon r={r} size={18} />} lead={lead} rest={rest} action={<Dismiss onClick={onDismiss} />} />
      {(lines.length > 0 || hidden) && (
        <ul style={{ margin: "0 0 0 30px", padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 2, ...muted }}>
          {lines.map((l, k) => <li key={k}>{l}</li>)}
          {(hidden || showAll) && <li><a onClick={() => setShowAll(!showAll)} style={{ cursor: "pointer", font: "700 13px var(--font-body)" }}>{showAll ? "Show fewer" : `Show all ${same.length + rejected.length}`}</a></li>}
        </ul>
      )}
    </div>
  );
}

/** The row: the icon, one sentence (a bold lead and the muted rest), and the action on the right. */
function Row({ icon, lead, rest, action }: { icon: ReactNode; lead: ReactNode; rest?: ReactNode; action?: ReactNode }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <span style={{ flex: "none", display: "flex" }}>{icon}</span>
      <span style={{ flex: 1, minWidth: 0, font: "700 14px var(--font-body)", color: "var(--text-strong)", overflow: "hidden", textOverflow: "ellipsis" }}>
        {lead}{rest ? <span style={{ fontWeight: 400, color: "var(--text-muted)" }}> {rest}</span> : null}
      </span>
      {action}
    </div>
  );
}

function Dismiss({ onClick }: { onClick: () => void }) {
  return <Button size="sm" variant="ghost" leadingIcon={<Icon name="x" size={14} />} onClick={onClick} aria-label="Dismiss">Dismiss</Button>;
}
